import { useQuery } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { createContext, useContext, useEffect, useMemo } from 'react'

import { BRANDING } from '@/endpoints'
import axiosInstance from '@/lib/axios'
import { brandFavicon } from '@/theme/brands'

// Mirrors the backend platform default brand (org/branding.py PLATFORM_DEFAULT_BRAND).
// Used while loading and whenever the host resolves to no partner.
//
// `is_partner` defaults to false deliberately: while branding is loading, or if the request
// fails, the public site shows the FULL platform experience (pricing, partner recruitment)
// rather than hiding it. Failing the other way would silently strip the platform's own
// marketing off its own homepage on any blip — a much worse and far less visible bug than
// briefly showing it. Marketing pages gate on `brand.is_partner` being truthy.
// `self_serve_signup_enabled` defaults false for the same reason `is_partner` defaults
// false: a stale cached brand, a branding fetch that hasn't resolved yet, or an older
// backend that doesn't send the field at all must all fail CLOSED here — the opposite
// direction from `is_partner` above, because the risk this flag guards (a public signup
// front door nobody meant to open) is the inverse of the risk `is_partner` guards (hiding
// the platform's own marketing). Missing/undefined must read as "not enabled".
export const PLATFORM_DEFAULT_BRAND = {
    product_name: 'Power',
    logo_url: '',
    primary_color: '',
    secondary_color: '',
    support_email: '',
    is_partner: false,
    self_serve_signup_enabled: false,
    // Platform brand look (theme/brands.js): 'joulelingo' on joulelingo.com,
    // 'kilosight' on kilosight.com, 'power' anywhere else.
    brand_key: 'power'
}

const BrandingContext = createContext({
    brand: PLATFORM_DEFAULT_BRAND,
    isLoading: false
})

const fetchBrand = async () => {
    const host = window?.location?.hostname || ''
    const { data } = await axiosInstance.get(BRANDING, { params: { host } })
    // Guard the shape. A misrouted request can resolve 200 with the SPA's index.html (this is
    // exactly what a relative '/api/branding/' did against the Vite dev server), and spreading
    // a string into the brand object yields numeric character keys rather than an error — the
    // brand then looks "default" instead of "broken", which hides the fault completely.
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        throw new Error('Branding endpoint returned a non-object response')
    }
    return data
}

export const BrandingProvider = ({ children }) => {
    // Public, unauthenticated resolve of host -> brand. Resolves once on load and is
    // long-lived; partner branding rarely changes within a session.
    const { data, isLoading } = useQuery({
        queryKey: ['branding', window?.location?.hostname || ''],
        queryFn: fetchBrand,
        staleTime: 60 * 60_000,
        // One retry, matching the plans query. With `retry: 0` a single transient failure
        // left `is_partner` false for the whole session, which on a partner's own domain
        // means platform pricing and reseller recruitment stay up until the visitor reloads.
        retry: 1
    })

    // Degrade gracefully: any failure or absent field falls back to the platform default.
    const brand = useMemo(
        () => ({ ...PLATFORM_DEFAULT_BRAND, ...(data || {}) }),
        [data]
    )

    useEffect(() => {
        if (brand.product_name) {
            document.title = brand.product_name
        }
        if (brand.primary_color) {
            // For non-MUI surfaces (plain CSS, previews). MUI-themed UI gets the
            // brand colour via App.jsx -> createTheme({brandPrimary}) instead.
            document.documentElement.style.setProperty(
                '--brand-primary',
                brand.primary_color
            )
        }
    }, [brand.product_name, brand.primary_color])

    // The server-rendered <head> already carries the brand favicon (base.html);
    // this covers the Vite dev shell and a brand that resolves after load.
    const favicon = brandFavicon(brand)
    useEffect(() => {
        if (!favicon) return
        const link = document.querySelector(
            'link[rel="icon"][type="image/svg+xml"]'
        )
        if (link && link.getAttribute('href') !== favicon) {
            link.setAttribute('href', favicon)
        }
    }, [favicon])

    const value = useMemo(() => ({ brand, isLoading }), [brand, isLoading])

    return (
        <BrandingContext.Provider value={value}>
            {children}
        </BrandingContext.Provider>
    )
}

BrandingProvider.propTypes = {
    children: PropTypes.node
}

export const useBranding = () => useContext(BrandingContext)

export default BrandingContext
